import type { FieldPack, FieldValues, PromptContext } from "../engine/types";

/**
 * Translation — the wedge field.
 *
 * The premise, from the horse's mouth: translation students cannot reliably write
 * "Translate this Arabic text to English." Worse, the ones who *can* still omit the
 * things that actually determine whether a translation is any good — register, text
 * type, audience, purpose.
 *
 * So we ask. Every question below exists because a translation brief that omits it
 * produces a worse translation. That is the entire value of this product: the
 * questions are the expertise, encoded once, reused forever.
 */

const LANGS = [
  { value: "ar", label: "العربية" },
  { value: "en", label: "الإنجليزية" },
  { value: "fr", label: "الفرنسية" },
  { value: "tr", label: "التركية" },
  { value: "fa", label: "الفارسية" },
  { value: "ku", label: "الكردية" },
  { value: "de", label: "الألمانية" },
  { value: "es", label: "الإسبانية" },
  { value: "ru", label: "الروسية" },
  { value: "zh", label: "الصينية" },
];

const LANG_NAME: Record<string, string> = Object.fromEntries(
  LANGS.map((l) => [l.value, l.label]),
);

export const translation: FieldPack = {
  id: "translation",
  title: "ترجمة",
  blurb: "ترجمة تراعي نوع النص وأسلوبه وجمهوره — مو بس تبديل كلمات.",
  icon: "translate",
  cost: 1,
  model: "gemini-2.5-flash",
  temperature: 0.3,

  inputs: [
    {
      name: "source_text",
      type: "textarea",
      label: "النص المراد ترجمته",
      hint: "الصق النص هنا. كل ما كان النص كامل، كل ما كانت الترجمة أدق — لا تقصّه بالنص.",
      placeholder: "الصق النص هنا…",
      required: true,
      maxLength: 20000,
    },
    {
      name: "from_lang",
      type: "select",
      label: "من أي لغة؟",
      hint: "إذا ما تعرف، خلّي «اكتشفها لي» ونحن نحددها من النص.",
      required: true,
      options: [{ value: "auto", label: "اكتشفها لي", hint: "نفحص النص ونحدد اللغة" }, ...LANGS],
      default: "auto",
      autodetect: {
        from: "source_text",
        prompt: (sample: string) =>
          `Identify the language of this text. Reply with ONLY one of these codes and nothing else: ${LANGS.map((l) => l.value).join(", ")}.\n\nText:\n${sample}`,
      },
    },
    {
      name: "to_lang",
      type: "select",
      label: "إلى أي لغة؟",
      required: true,
      options: LANGS,
      default: "en",
    },
    {
      name: "text_type",
      type: "select",
      label: "شنو نوع النص؟",
      hint: "هذا أهم سؤال بالاستمارة. نوع النص يقرر كل شي بعده — النص القانوني يُترجم حرفياً، والإعلان يُترجم بحرية.",
      required: true,
      options: [
        { value: "auto", label: "اكتشفه لي", hint: "نفحص النص ونحدد نوعه" },
        { value: "legal", label: "قانوني", hint: "عقد، حكم، قانون — الدقة الحرفية قبل السلاسة" },
        { value: "academic", label: "أكاديمي", hint: "بحث، أطروحة، مقال علمي" },
        { value: "medical", label: "طبي", hint: "تقرير، وصفة، دراسة سريرية" },
        { value: "technical", label: "تقني", hint: "دليل استخدام، مواصفات، توثيق" },
        { value: "journalistic", label: "صحفي", hint: "خبر، تقرير، مقال رأي" },
        { value: "literary", label: "أدبي", hint: "رواية، قصة، شعر — الأثر قبل الحرف" },
        { value: "marketing", label: "تسويقي", hint: "إعلان، محتوى ترويجي — يُنقل الأثر لا الكلمات" },
        { value: "official", label: "رسمي/إداري", hint: "مخاطبة، وثيقة حكومية، شهادة" },
        { value: "conversational", label: "محادثة", hint: "رسائل، حوار يومي" },
        { value: "religious", label: "ديني", hint: "نص شرعي أو خطاب ديني" },
      ],
      default: "auto",
      autodetect: {
        from: "source_text",
        prompt: (sample: string) =>
          `Classify this text into exactly ONE category. Reply with ONLY the category key and nothing else. Keys: legal, academic, medical, technical, journalistic, literary, marketing, official, conversational, religious.\n\nText:\n${sample}`,
      },
    },
    {
      name: "register",
      type: "radio",
      label: "شنو مستوى الأسلوب المطلوب؟",
      hint: "الطالب عادةً ينسى هذا، فتطلع ترجمة رسمية لنص عامي أو بالعكس.",
      required: true,
      options: [
        { value: "match", label: "مثل الأصل", hint: "نحافظ على مستوى النص الأصلي — الخيار الآمن" },
        { value: "formal", label: "رسمي", hint: "أكثر رسمية من الأصل" },
        { value: "neutral", label: "محايد", hint: "لا رسمي ولا عامي" },
        { value: "casual", label: "بسيط/ودّي", hint: "أسهل وأقرب للقارئ العادي" },
      ],
      default: "match",
    },
    {
      name: "audience",
      type: "text",
      label: "منو الجمهور؟ (اختياري بس ينفع هواي)",
      hint: "مثلاً: «طلاب جامعة»، «محكمة»، «زبائن على إنستغرام». الجمهور يقرر اختيار المفردة.",
      placeholder: "مثلاً: قارئ عام غير متخصص",
      maxLength: 200,
    },
    {
      name: "purpose",
      type: "select",
      label: "الترجمة لأي غرض؟",
      hint: "الترجمة للنشر غير الترجمة للفهم. الغرض يقرر كم نتحرر من الأصل.",
      options: [
        { value: "publication", label: "للنشر", hint: "تطلع كأنها مكتوبة أصلاً باللغة الهدف" },
        { value: "understanding", label: "للفهم فقط", hint: "المهم توصل المعنى" },
        { value: "official_submission", label: "لتقديم رسمي", hint: "دقة حرفية، لا تصرّف" },
        { value: "study", label: "للدراسة/التدريب", hint: "نشرح الخيارات حتى تتعلم" },
      ],
      default: "publication",
    },
    {
      name: "dialect_target",
      type: "select",
      label: "أي لهجة/متغير باللغة الهدف؟",
      hint: "الإنجليزية البريطانية غير الأمريكية بالإملاء والمفردة.",
      options: [
        { value: "standard", label: "المعياري/الفصيح" },
        { value: "en_us", label: "إنجليزية أمريكية" },
        { value: "en_uk", label: "إنجليزية بريطانية" },
        { value: "ar_msa", label: "عربية فصحى" },
        { value: "ar_iraqi", label: "عربية عراقية (عامية)" },
      ],
      default: "standard",
    },
    {
      name: "glossary",
      type: "textarea",
      label: "مصطلحات ثابتة؟ (اختياري)",
      hint: "سطر لكل مصطلح: «الأصل = الترجمة». نلتزم بيها حرفياً ولا نجتهد.",
      placeholder: "شركة المساهمة = joint-stock company\nالوقائع العراقية = Al-Waqai Al-Iraqiya",
      maxLength: 3000,
    },
    {
      name: "preserve_formatting",
      type: "toggle",
      label: "حافظ على التنسيق (فقرات، ترقيم، عناوين)",
      default: true,
    },
    {
      name: "keep_names",
      type: "toggle",
      label: "اترك أسماء الأعلام بلغتها الأصلية بين قوسين",
      hint: "ينفع بالنصوص القانونية والأكاديمية حتى يقدر القارئ يرجع للأصل.",
      default: false,
    },
    {
      name: "explain_choices",
      type: "toggle",
      label: "اشرح لي قرارات الترجمة",
      hint: "للطلاب: نوضّح ليش اخترنا مفردة على أخرى. هذا اللي يفرّق بين أداة وبين معلّم.",
      default: false,
      showIf: (v: FieldValues) => v.purpose === "study" || v.purpose === "publication",
    },
  ],

  output: {
    kind: "parallel",
    title: "الترجمة",
    copyable: true,
    sourceInput: "source_text",
  },

  buildPrompt(values: FieldValues, ctx: PromptContext) {
    const detected = ctx.detected ?? {};
    const from = (detected.from_lang ?? values.from_lang) as string;
    const to = values.to_lang as string;
    const textType = (detected.text_type ?? values.text_type) as string;

    const fromName = LANG_NAME[from] ?? from;
    const toName = LANG_NAME[to] ?? to;

    // Text type is the master variable — it sets the literal/free dial.
    const TYPE_GUIDANCE: Record<string, string> = {
      legal:
        "This is a LEGAL text. Fidelity outranks fluency. Do not smooth, summarize, or resolve ambiguity — if the source is ambiguous, the translation must be equally ambiguous. Preserve sentence boundaries and clause structure. Use established legal equivalents, not descriptive paraphrase. Never drop a qualifier.",
      academic:
        "This is an ACADEMIC text. Preserve hedging and epistemic stance exactly (may/might/suggests are not interchangeable). Keep terminology consistent throughout — never vary a term for stylistic variety. Preserve citations verbatim.",
      medical:
        "This is a MEDICAL text. Precision is a safety matter. Use standard clinical terminology. Never approximate a dosage, unit, or drug name. If a term is ambiguous, flag it rather than guessing.",
      technical:
        "This is a TECHNICAL text. Terminology must be consistent and standard. Preserve code, identifiers, units, and product names exactly. Prefer the industry-accepted term over a literal rendering.",
      journalistic:
        "This is JOURNALISTIC text. Match news register in the target language. Keep attributions precise. Render quotes naturally but never alter their meaning.",
      literary:
        "This is a LITERARY text. Effect outranks literal fidelity. Recreate voice, rhythm, and imagery. A line that is accurate but dead is a failed translation. Preserve ambiguity that the author chose.",
      marketing:
        "This is MARKETING copy. Translate the EFFECT, not the words. A literal rendering will fail. Adapt idiom, wordplay, and cultural reference to land for the target audience. Keep it short and punchy.",
      official:
        "This is an OFFICIAL/ADMINISTRATIVE document. Use the fixed formulae of the target language's bureaucratic register. Preserve all names, numbers, dates, and reference codes exactly.",
      conversational:
        "This is CONVERSATIONAL text. Render it as a real person would say it in the target language. Contractions and idiom are correct here.",
      religious:
        "This is a RELIGIOUS text. Handle with care and precision. Use established renderings of canonical terms. Do not paraphrase doctrine. Where a term is contested, prefer the widely accepted rendering and note alternatives.",
    };

    const REGISTER_GUIDANCE: Record<string, string> = {
      match: "Match the source's register exactly. Do not elevate or lower it.",
      formal: "Raise the register above the source: formal, no contractions, no idiom.",
      neutral: "Neutral register: neither formal nor casual.",
      casual: "Lower the register: plain, friendly, accessible to a general reader.",
    };

    const PURPOSE_GUIDANCE: Record<string, string> = {
      publication:
        "The result must read as though originally written in the target language. No translationese.",
      understanding: "Prioritize clear conveyance of meaning over polish.",
      official_submission:
        "This will be submitted to an official body. Maximum literal fidelity. Do not improve the source.",
      study:
        "The reader is a translation student. Accuracy plus pedagogical transparency.",
    };

    const DIALECT_GUIDANCE: Record<string, string> = {
      standard: "",
      en_us: "Use American English spelling and vocabulary (color, organize, elevator).",
      en_uk: "Use British English spelling and vocabulary (colour, organise, lift).",
      ar_msa: "Use Modern Standard Arabic (فصحى).",
      ar_iraqi: "Use Iraqi colloquial Arabic as actually spoken.",
    };

    const parts: string[] = [
      "You are an expert translator. You produce publication-grade translations and you follow a brief exactly.",
      "",
      `TASK: Translate from ${fromName} to ${toName}.`,
      "",
      TYPE_GUIDANCE[textType] ?? "",
      REGISTER_GUIDANCE[values.register as string] ?? "",
      PURPOSE_GUIDANCE[values.purpose as string] ?? "",
      DIALECT_GUIDANCE[values.dialect_target as string] ?? "",
    ];

    if (values.audience) parts.push(`AUDIENCE: ${values.audience}. Choose vocabulary they will understand.`);
    if (values.preserve_formatting)
      parts.push("FORMATTING: Preserve paragraph breaks, numbering, headings, and lists exactly.");
    if (values.keep_names)
      parts.push('PROPER NOUNS: After each translated proper noun, give the original in parentheses — e.g. Baghdad (بغداد).');
    if (values.glossary) {
      parts.push(
        "GLOSSARY — these renderings are MANDATORY. Use them exactly, every time, even if you would choose differently:",
        String(values.glossary),
      );
    }

    parts.push(
      "",
      "HARD RULES:",
      "- Translate the ENTIRE text. Never truncate, summarize, or add an editorial note about length.",
      "- Do not add content that is not in the source. Do not omit content that is.",
      "- If a passage is genuinely untranslatable or ambiguous, translate your best reading and note it at the end — do not silently guess.",
      "- Output the translation ONLY. No preamble, no 'Here is the translation'.",
    );

    if (values.explain_choices) {
      parts.push(
        "",
        "After the translation, add a line containing exactly ---NOTES--- and then briefly explain your significant choices: terms with several possible renderings and why you chose yours, anything lost or compensated, and any ambiguity in the source. Be specific and short. Write these notes in Arabic.",
      );
    }

    return {
      system: parts.filter(Boolean).join("\n"),
      user: String(values.source_text ?? ""),
    };
  },
};
